import styled from 'styled-components';

import type { User } from '@wecasa/common/src/types/commons';
import { getCurrentCountry } from '@wecasa/common/src/utils/country';
import { formatPercentage, formatPricePerHour } from '@wecasa/common/src/utils/formatting';

import { translate } from '@wecasa/i18n';

import { MEMBERSHIP_CONFIG_BY_COUNTRY } from '@wecasa/membership/src/constants/country-config';
import { getMembershipQuestions } from '@wecasa/membership/src/utils/membership-paywall';

import Text from '@wecasa/web-ui/src/components/base-text/variants/text';
import Title from '@wecasa/web-ui/src/components/base-text/variants/title';
import Button from '@wecasa/web-ui/src/components/button/button';
import { minWidth } from '@wecasa/web-ui/src/styles/mixins';

import Advantages from '../advantages';
import {
  Content,
  MembershipBackground,
  MembershipFaq,
  StickyContainer,
  StyledStickyBottomWrapper,
  StyledWhiteSpacerRoundedBorders,
} from '../common';

const t = translate('modals.membership_paywall');

const StyledTitle = styled(Title.LG)`
  margin: 20px 0;

  ${minWidth.md`
    margin: 0 0 20px;
  `}
`;

const Subtitle = styled.div`
  margin-bottom: 20px;
`;

type RecurringContentProps = {
  user?: User;
  closePaywall?: () => void;
};

const RecurringContent = ({ user, closePaywall }: RecurringContentProps) => {
  const currentCountry = getCurrentCountry();

  const membershipConfig = MEMBERSHIP_CONFIG_BY_COUNTRY[currentCountry];
  const formattedWellnessMemberReductionPercentage = formatPercentage(membershipConfig.wellnessReductionsPercentage);
  const formattedCleaningMemberHourlyReduction = formatPricePerHour(membershipConfig.cleaningMaxPricePerHour, {
    precision: 0,
  });

  const advantages = [
    t('.advantages.wellness.prestation_reductions', {
      reductionsInPercentage: formattedWellnessMemberReductionPercentage,
    }),
    t('.advantages.cleaning.prestation_reductions', {
      pricePerHour: formattedCleaningMemberHourlyReduction,
    }),
  ];

  return (
    <StickyContainer>
      <Content>
        <MembershipBackground>
          <StyledTitle>{t('.title.cleaning.recurring')}</StyledTitle>
          <Subtitle>
            <Text>{t('.subtitle.cleaning.recurring')}</Text>
          </Subtitle>
          <Advantages advantages={advantages} />
        </MembershipBackground>
        <StyledWhiteSpacerRoundedBorders />
        <MembershipFaq
          title={t('.faq.title')}
          questions={getMembershipQuestions({
            universeReference: 'cleaning',
            isSpotCleaning: false,
            user,
          })}
        />
      </Content>
      <StyledStickyBottomWrapper>
        <Button size='lg' onPress={closePaywall} hasFullWidth>
          {t('base.got_it')}
        </Button>
      </StyledStickyBottomWrapper>
    </StickyContainer>
  );
};

export default RecurringContent;
